import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import Breadcrumbs from '../components/breadcrumbs'
import Footer from '../components/footer'
import DarkNav from '../components/dark-nav'
import { getCanonicalUrl } from '../utils/canonical'
import './terms-of-use.css'

const TermsOfUse = () => {
  useEffect(() => {
    window.scrollTo(0, 0);

    // Initialize AOS animations if available
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-out',
        once: false,
        offset: 100,
        delay: 0,
        mirror: true
      });
    }
  }, []);

  // Breadcrumb navigation
  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Terms of Use' }
  ]

  return (
    <div className="terms-of-use-container">
      <Helmet>
        <title>Terms of Use | DataFlows Australia</title>
        <link rel="canonical" href={getCanonicalUrl('/terms-of-use')} />
        <meta
          name="description"
          content="Terms of Use for DataFlows Australia SMS services. Read our terms and conditions for using our business SMS platform and services."
        />
        <meta property="og:title" content="Terms of Use | DataFlows Australia" />
        <meta
          property="og:description"
          content="Terms of Use for DataFlows Australia SMS services and platform."
        />
      </Helmet>

      <DarkNav />

      {/* Breadcrumbs */}
      <div className="terms-breadcrumbs">
        <div className="container">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      {/* Hero Section */}
      <section className="terms-hero">
        <div className="container">
          <div className="hero-content" data-aos="fade-up">
            <h1>Terms of Use</h1>
            <p className="hero-subtitle">
              Please read these terms and conditions carefully before using DataFlows Australia services.
            </p>
            <div className="last-updated">
              <span>Last updated: January 2025</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="terms-content">
        <div className="container">
          <div className="content-wrapper">

            <div className="terms-section" data-aos="fade-up">
              <h2>1. Acceptance of Terms</h2>
              <p>
                By accessing and using DataFlows Australia's SMS services ("Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </div>

            <div className="terms-section" data-aos="fade-up">
              <h2>2. Service Description</h2>
              <p>
                DataFlows Australia provides business SMS messaging services, including but not limited to:
              </p>
              <ul>
                <li>Bulk SMS messaging capabilities</li>
                <li>Two-way SMS communication</li>
                <li>API access for SMS automation</li>
                <li>Virtual phone number services</li>
                <li>Integration tools and connectors</li>
              </ul>
            </div>

            <div className="terms-section" data-aos="fade-up">
              <h2>3. User Responsibilities</h2>
              <p>
                When using our services, you agree to:
              </p>
              <ul>
                <li>Comply with all applicable Australian laws and regulations</li>
                <li>Obtain proper consent before sending SMS messages to recipients</li>
                <li>Not use the service for spam, harassment, or illegal activities</li>
                <li>Maintain the security of your account credentials</li>
                <li>Provide accurate and up-to-date account information</li>
              </ul>
            </div>

            <div className="terms-section" data-aos="fade-up">
              <h2>4. Prohibited Uses</h2>
              <p>
                You may not use our service for:
              </p>
              <ul>
                <li>Sending unsolicited commercial messages (spam)</li>
                <li>Transmitting harmful, threatening, or inappropriate content</li>
                <li>Violating any laws or regulations</li>
                <li>Infringing on intellectual property rights</li>
                <li>Attempting to gain unauthorized access to our systems</li>
              </ul>
            </div>

            <div className="terms-section" data-aos="fade-up">
              <h2>5. Privacy and Data Protection</h2>
              <p>
                We are committed to protecting your privacy and personal information. Our collection, use, and disclosure of personal information is governed by our Privacy Policy, which forms part of these Terms of Use.
              </p>
              <p>
                <Link to="/privacy-policy" className="privacy-link">Read our Privacy Policy →</Link>
              </p>
            </div>

            <div className="terms-section" data-aos="fade-up">
              <h2>6. Payment and Billing</h2>
              <ul>
                <li>All charges are in Australian Dollars (AUD) unless otherwise specified</li>
                <li>Payment is required in advance for SMS credits</li>
                <li>Refunds may be provided at our discretion in accordance with our refund policy</li>
                <li>You are responsible for all charges incurred under your account</li>
              </ul>
            </div>

            <div className="terms-section" data-aos="fade-up">
              <h2>7. Service Availability</h2>
              <p>
                While we strive to provide reliable service, we do not guarantee:
              </p>
              <ul>
                <li>100% uptime or availability</li>
                <li>Delivery of all messages</li>
                <li>Specific delivery timeframes</li>
                <li>Compatibility with all carriers or networks</li>
              </ul>
            </div>

            <div className="terms-section" data-aos="fade-up">
              <h2>8. Limitation of Liability</h2>
              <p>
                DataFlows Australia shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the service.
              </p>
            </div>

            <div className="terms-section" data-aos="fade-up">
              <h2>9. Termination</h2>
              <p>
                We may terminate or suspend your account and access to the service immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.
              </p>
            </div>

            <div className="terms-section" data-aos="fade-up">
              <h2>10. Changes to Terms</h2>
              <p>
                We reserve the right to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days notice prior to any new terms taking effect.
              </p>
            </div>

            <div className="terms-section" data-aos="fade-up">
              <h2>11. Governing Law</h2>
              <p>
                These Terms shall be interpreted and governed by the laws of Australia. Any disputes relating to these terms and conditions will be subject to the jurisdiction of the Australian courts.
              </p>
            </div>

            <div className="terms-section" data-aos="fade-up">
              <h2>12. Contact Information</h2>
              <p>
                If you have any questions about these Terms of Use, please contact us:
              </p>
              <div className="contact-info">
                <p><strong>DataFlows Australia</strong></p>
                <p>Email: <a href="mailto:support@dataflows.com.au">support@dataflows.com.au</a></p>
                <p>Website: <a href="https://dataflows.com.au">https://dataflows.com.au</a></p>
                <p><Link to="/contact-us">Contact Us Form →</Link></p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default TermsOfUse